import React, { useEffect, useState } from "react";
import Breadcrumb from "../../../../Components/Common/Breadcrumb";
import { faAdd, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { liveClassTabsData } from "../../../../data/SidebarData";
import Tabs from "../../../../Components/Common/Tabs";
import Table from "../../../../Components/Common/Table";
import TableHead from "../../../../Components/Common/TableHead";
import useSWR from "swr";
import { liveClassItemsUrls } from "../../../../Api/Auth/ApiUrls";
import QueryString from "qs";
import useTable from "../../../../hooks/useTable";
import useToastError from "../../../../hooks/useErrorToast";
import { Loader } from "../../../../Components/Common/Loader";
import NoData from "../../../../Components/Common/NoData";
import { toast } from "react-toastify";
import NotFound from "../../../Notfound";
import Pagination from "../../../../Components/Common/Pagination/Pagination";
import LiveClassItemsRow from "./Components/LiveClassItemsRow";
import Modal from "../../../../Components/Common/Modal";
import LiveClassItemsForm from "./Components/LiveClassItemsForm";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../../Api/swrConfig";
import Storage from "../../Storage";

const tableColumnData = [
  { id: "title", label: "Title" },
  { id: "duration", label: "Duration" },
  { id: "date", label: "Date" },
  { id: "time", label: "Time" },
  { id: "instructor", label: "Instructor" },
  { id: "meetingLink", label: "MeetingLink" },
  { id: "recordingLink", label: "RecordingLink" },
  { id: "createdAt", label: "Created" },
];

const LiveClassItem = () => {
  const [row, setRow] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "12:00 AM",
    duration: "",
    instructor: "",
    meetingLink: "",
    recordingLink: "",
  });
  const [isModalOpen, setModalOpen] = useState(false);
  const [isStorageModalOpen, setStorageModalOpen] = useState(false);

  const params = useParams();
  const { id } = params;
  const { showErrorToast } = useToastError();
  const location = useLocation();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });
  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };

  const apiQueryString = QueryString.stringify(apiQueryParams);

  const { isLoading, data, mutate, error } = useSWR(
    liveClassItemsUrls.list(id, apiQueryString)
  );

  useEffect(() => {
    if (error?.message) {
      if (error.message === "Request failed with status code 400") {
        toast.error("Live Class Subscription not found");
      }
    }
  }, [error]);

  const handleInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };

  const onEditClick = (row) => {
    setRow(row);
    handleInputChange("title", row.title);
    handleInputChange("description", row.description);
    handleInputChange("date", row.date);
    handleInputChange("time", row.time);
    handleInputChange("duration", row.duration);
    handleInputChange("instructor", row.instructor);
    handleInputChange("meetingLink", row.meetingLink);
    handleInputChange("recordingLink", row.recordingLink);
    setModalOpen(true);
  };

  const navigate = useNavigate();
  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          title: "",
          description: "",
          date: "",
          time: "12:00 AM",
          duration: "",
          instructor: "",
          meetingLink: "",
          recordingLink: "",
        });
        setModalOpen(true);
      },
    },
    {
      label: "Back",
      icon: faArrowLeft,
      onClick: () => {
        navigate(-1);
      },
    },
  ];

  const { trigger, isMutating } = useSWRMutation(
    liveClassItemsUrls.create(id),
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );
  const { trigger: EditTrigger, isMutating: editMutating } = useSWRMutation(
    liveClassItemsUrls.edit(id, row?._id),
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.duration) newErrors.duration = "Duration is required";
    if (!formData.time) newErrors.time = "Time is required";
    if (!formData.date) newErrors.date = "Start Date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log(formData);

    if (!validateForm()) return;
    const modifiedData = {
      ...formData,
      recordingLink: Array.isArray(formData.recordingLink)
        ? ""
        : formData.recordingLink,
      date: formData.date,
    };

    row ? EditTrigger(modifiedData) : trigger(modifiedData);
  };

  if (error?.message === "Request failed with status code 400") {
    return <NotFound />;
  }

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Tabs tabs={liveClassTabsData} />
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <LiveClassItemsRow
                  key={row._id}
                  row={row}
                  mutate={mutate}
                  onEditClick={onEditClick}
                />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={row ? "Edit Live Class" : "Create Live Class"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        formWidth
      >
        <LiveClassItemsForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
          setModalOpen={setStorageModalOpen}
        />
      </Modal>
      <Modal
        isOpen={isStorageModalOpen}
        title={"Storage Manager"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setStorageModalOpen(false);
        }}
        footer={false}
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length)
              handleInputChange("recordingLink", file[0]?.fileUrl);
            setStorageModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default LiveClassItem;
